import { ChangeEvent, useEffect, useRef, useState } from 'react';

import {
  AngleDown,
  AngleUp,
  ChakraBox,
  ChakraButton,
  ChakraCollapse,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraSpacer,
  ChakraText,
  Checkbox,
  Edit,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Info,
} from '@equip.health/ui';

import { PatientEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  DiagnosisStatus,
  DiagnosisStatusCode,
  diagnosisStatusDefinitions,
  isVoidOrResolvedOrInactive,
} from '~/lib/util/clinicalProfile.util';

import AddOrEditDiagnosis from './AddOrEditDiagnosis';
import ViewDiagnosis from './ViewDiagnosis';

interface DiagnosisListItemProps {
  diagnosis: PatientDiagnosis;
  diagnosisIdToEdit: string;
  isNewDiagnosis?: boolean;
  isPrimaryDiagnosisExists: boolean;
  onAdd: () => void;
  onDiscard?: () => void;
  patientId?: string;
  validatedDiagnosisHasCodeAlready: (
    diagnosisCode: string,
    updatedStatusCode: string,
    skipDiagnosisId?: string,
  ) => boolean;
  setDiagnosisIdToEdit: (diagnosesIdToEdit: string) => void;
}

const { h6, label, labelBold } = EquipUITheme.typography;
const { primary, tertiary, neutral } = EquipUIFireflyV2Theme.colors;

const DiagnosisListItem = ({
  diagnosis,
  diagnosisIdToEdit,
  isNewDiagnosis,
  isPrimaryDiagnosisExists,
  onAdd,
  onDiscard,
  patientId,
  validatedDiagnosisHasCodeAlready,
}: DiagnosisListItemProps) => {
  const {
    diagnosis: diagnosisDetails,
    externalId,
    isAddedFromSurvey,
    statusLabel,
    statusCode,
    primary: isPrimaryDiagnosis,
  } = diagnosis || {};
  const { code, shortDescription, eatingDisorder } = diagnosisDetails;
  const PATIENT_DIAGNOSIS_IDENTIFIER = `patient-diagnosis__${
    isNewDiagnosis ? 'new' : externalId
  }`;
  const getInitialIsSelectedAsPrimaryDiagnosisValue = (): boolean =>
    isVoidOrResolvedOrInactive(statusCode)
      ? false
      : !!isPrimaryDiagnosis ||
        (!isPrimaryDiagnosisExists && isNewDiagnosis && eatingDisorder);

  const [isExpanded, setIsExpanded] = useState<boolean>(!!isNewDiagnosis);
  const [isSelectedAsPrimaryDiagnosis, setIsSelectedAsPrimaryDiagnosis] =
    useState<boolean>(getInitialIsSelectedAsPrimaryDiagnosisValue());

  const [isEditMode, setIsEditMode] = useState<boolean>(!!isNewDiagnosis);
  const [highlighted, setHighlighted] = useState<boolean>(false);

  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (diagnosisIdToEdit && diagnosisIdToEdit === diagnosis.externalId) {
      setIsEditMode(true);
      setIsExpanded(true);
      if (itemRef.current) {
        itemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      setHighlighted(true);
      const timeoutId = setTimeout(() => setHighlighted(false), 1000);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return undefined;
  }, [diagnosisIdToEdit, diagnosis.externalId]);

  useState<boolean>(false);
  const { trackEvent } = useAnalytics();
  const handleTrackEvent = () => {
    trackEvent(PatientEvents.medicalHistoryEditButtonClicked, {
      Source: AnalyticsSourceValues.CLINICAL_PROFILE_TAB,
    });
  };

  const handleToggle = (): void => setIsExpanded(!isExpanded);
  const isEditDisabled = statusLabel === DiagnosisStatus.VOID;
  const [isSetAsPrimaryDisabled, setIsSetAsPrimaryDisabled] = useState<boolean>(
    isVoidOrResolvedOrInactive(statusCode),
  );
  const onStatusUpdate = (updatedStatusCode: string): void => {
    if (isVoidOrResolvedOrInactive(updatedStatusCode as DiagnosisStatusCode)) {
      setIsSelectedAsPrimaryDiagnosis(false);
      setIsSetAsPrimaryDisabled(true);
    } else {
      setIsSetAsPrimaryDisabled(false);
    }
  };
  const StatusLabel = () => {
    const { color, textColor, title } =
      diagnosisStatusDefinitions[statusLabel ?? DiagnosisStatus.PRELIMINARY];
    return !title ? null : (
      <ChakraBox
        background={color}
        border={
          statusLabel === DiagnosisStatus.PRELIMINARY
            ? '1px dashed rgba(0, 0, 0, 0.1)'
            : 'none'
        }
        borderRadius="8px"
        fontSize="10px"
        fontWeight="700"
        height="20px"
        id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__status-label`}
        padding="2px 8px"
        width="fit-content"
      >
        <ChakraText as="h6" color={textColor} fontSize="10px">
          {title}
        </ChakraText>
      </ChakraBox>
    );
  };

  return (
    <>
      <ChakraSimpleGrid
        borderColor={highlighted ? 'yellow.300' : 'neutral.line'}
        borderRadius="16px"
        borderStyle="solid"
        borderWidth="2px"
        gap={2}
        id={`${PATIENT_DIAGNOSIS_IDENTIFIER}`}
        marginY="5px"
        padding="23.5px 16px"
        ref={itemRef}
      >
        <ChakraGridItem>
          <ChakraFlex alignItems="center">
            <ChakraBox>
              <ChakraText
                {...h6}
                id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__diagnosis-name`}
              >
                {shortDescription}
              </ChakraText>
              <ChakraFlex marginTop="2px">
                <ChakraText
                  {...label}
                  color="neutral.secondary"
                  id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__diagnosis-code`}
                  marginRight="8px"
                >
                  {code}
                </ChakraText>

                {isPrimaryDiagnosis && !isEditMode && (
                  <ChakraBox
                    background="primary.100"
                    borderRadius="8px"
                    fontSize="10px"
                    fontWeight="700"
                    height="20px"
                    id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__status-label`}
                    padding="2px 8px"
                    width="fit-content"
                  >
                    <ChakraText as="h6" color="white" fontSize="10px">
                      Primary
                    </ChakraText>
                  </ChakraBox>
                )}

                {isExpanded && isEditMode && (
                  <Checkbox
                    id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__set-as-primary`}
                    isChecked={isSelectedAsPrimaryDiagnosis}
                    isDisabled={isSetAsPrimaryDisabled}
                    label="Set as primary diagnosis"
                    labelColor="neutral.secondary"
                    labelFontSize="12px"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setIsSelectedAsPrimaryDiagnosis(event.target.checked)
                    }
                  />
                )}
              </ChakraFlex>
            </ChakraBox>
            <ChakraSpacer />
            {isAddedFromSurvey && (
              <ChakraFlex
                alignItems="center"
                backgroundColor={tertiary.marigold.base}
                borderRadius="100px"
                height="22px"
                id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__added-via-survey`}
                justifyContent="center"
                marginRight="12px"
                padding="4px 8px"
              >
                <Info fill={neutral.primary} height="12px" width="12px" />
                <ChakraText
                  color="neutral.primary"
                  fontSize="10px"
                  fontWeight="700"
                  id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__added-via-survey-label`}
                  marginLeft="4px"
                >
                  Added via survey response
                </ChakraText>
              </ChakraFlex>
            )}
            {!isEditMode && <StatusLabel />}
            <ChakraButton
              _hover={{ background: 'transparent' }}
              background="transparent"
              color="neutral.tertiary"
              fontWeight="700"
              id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__expand-icon`}
              marginLeft="10px"
              onClick={handleToggle}
              rightIcon={
                isExpanded ? (
                  <AngleUp height="12px" width="12px" />
                ) : (
                  <AngleDown height="12px" width="12px" />
                )
              }
              size="xs"
            />
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraCollapse in={isExpanded} style={{ overflow: 'unset' }}>
          <>
            {isEditMode ? (
              <AddOrEditDiagnosis
                diagnosis={{
                  ...diagnosis,
                  primary: isSelectedAsPrimaryDiagnosis,
                }}
                onAdd={() => {
                  onAdd();
                  setIsExpanded(false);
                  setIsEditMode(false);
                }}
                onDiscard={
                  isNewDiagnosis ? onDiscard : () => setIsEditMode(false)
                }
                onStatusUpdate={onStatusUpdate}
                patientId={patientId}
                primaryDiagnosisExists={isPrimaryDiagnosisExists}
                validatedDiagnosisHasCodeAlready={
                  validatedDiagnosisHasCodeAlready
                }
              />
            ) : (
              <ViewDiagnosis diagnosis={diagnosis} />
            )}
            {!isEditMode && (
              <ChakraFlex
                alignItems="center"
                justifyContent="flex-start"
                marginTop="30px"
              >
                <ChakraButton
                  {...labelBold}
                  background="neutral.background.secondary"
                  border={`1px solid ${neutral.background.secondary}`}
                  borderRadius="8px"
                  color="neutral.secondary"
                  height="30px"
                  id={`${PATIENT_DIAGNOSIS_IDENTIFIER}__edit__button`}
                  isDisabled={isEditDisabled}
                  onClick={() => {
                    handleTrackEvent();
                    setIsEditMode(true);
                  }}
                  padding="7px, 6px, 6px, 6px"
                  rightIcon={
                    <Edit fill={primary[100]} height="16px" width="16px" />
                  }
                  variant="ghost"
                >
                  Edit
                </ChakraButton>
              </ChakraFlex>
            )}
          </>
        </ChakraCollapse>
      </ChakraSimpleGrid>
    </>
  );
};
export default DiagnosisListItem;
